import React, { useEffect, useState } from "react";
import api, { brl, fmtDate } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

const EVENTOS = { criacao: "Criação", assinatura: "Assinatura", baixa: "Baixa", baixa_parcial: "Baixa parcial", estorno: "Estorno", cancelamento: "Cancelamento", termo_cancelamento: "Termo de Cancelamento gerado", recalculo: "Recálculo", recalculo_manual: "Recálculo manual", exclusao: "Exclusão" };
const fdt = (s) => (s ? new Date(s).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" }) : "-");

export const CancelarAcordoDialog = ({ acordo, onClose, onDone }) => {
  const [motivo, setMotivo] = useState("");
  const [busy, setBusy] = useState(false);
  const temPagamento = Number(acordo?.total_pago || 0) > 0;
  const confirmar = async () => {
    if (motivo.trim().length < 3) { toast.error("Informe o motivo do cancelamento"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/acordos/${acordo.id}/cancelar`, { motivo: motivo.trim() });
      toast.success(data.parcelas_originais_restauradas ? "Acordo cancelado. Parcelas originais restauradas." : "Acordo cancelado. Pagamentos e histórico preservados — acerto financeiro manual necessário.");
      onDone?.(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao cancelar acordo"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="cancelar-acordo-dialog">
        <DialogHeader>
          <DialogTitle>Cancelar acordo — {acordo.cliente_nome}</DialogTitle>
          <DialogDescription>Saldo em aberto {brl(acordo.saldo_aberto)} · pago {brl(acordo.total_pago)} · {acordo.parcelas_pagas}/{acordo.parcelas_total} parcelas pagas</DialogDescription>
        </DialogHeader>
        <div className={`rounded-lg border p-3 text-sm ${temPagamento ? "border-amber-300 bg-amber-50 text-amber-900" : "border-blue-200 bg-blue-50 text-blue-900"}`} data-testid="cancelar-acordo-aviso">
          {temPagamento
            ? "Este acordo já recebeu pagamentos. As parcelas originais NÃO serão restauradas automaticamente; pagamentos e histórico serão preservados e o acerto financeiro deverá ser feito manualmente."
            : "Sem pagamentos registrados: as parcelas originais renegociadas voltarão ao estado anterior e as parcelas do acordo ficarão canceladas."}
        </div>
        <Textarea value={motivo} onChange={(e) => setMotivo(e.target.value)} rows={3} placeholder="Motivo do cancelamento (obrigatório)" data-testid="cancelar-acordo-motivo" />
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} data-testid="cancelar-acordo-voltar">Voltar</Button>
          <Button onClick={confirmar} disabled={busy || motivo.trim().length < 3} className="bg-red-600 hover:bg-red-700 text-white" data-testid="cancelar-acordo-confirmar">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirmar cancelamento"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const ExcluirAcordoDialog = ({ acordo, onClose, onDone }) => {
  const [motivo, setMotivo] = useState("");
  const [busy, setBusy] = useState(false);
  const [pv, setPv] = useState(null);
  const [erro, setErro] = useState("");
  useEffect(() => { api.get(`/acordos/${acordo.id}/excluir-preview`).then((r) => setPv(r.data)).catch((e) => setErro(e.response?.data?.detail || "Erro ao calcular consequências")); }, [acordo.id]);
  const confirmar = async () => {
    if (busy || motivo.trim().length < 3) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/acordos/${acordo.id}/excluir`, { motivo: motivo.trim() });
      toast.success(data.ja_excluido ? "Acordo já estava excluído." : "Acordo excluído. Parcelas do acordo retiradas das listas ativas; histórico e pagamentos preservados.");
      onDone?.(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao excluir acordo"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="excluir-acordo-dialog">
        <DialogHeader>
          <DialogTitle className="break-words">Excluir acordo — {acordo.cliente_nome}</DialogTitle>
          <DialogDescription>Exclusão lógica: o acordo sai das listas ativas, mas histórico, termo e pagamentos são preservados. Excluir não perdoa a dívida.</DialogDescription>
        </DialogHeader>
        {erro && <p className="text-sm text-red-600" data-testid="excluir-acordo-erro">{erro}</p>}
        {!pv && !erro ? <div className="py-6 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div> : pv && (
          <div className="space-y-2 text-sm">
            {pv.bloqueio && <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-red-900" data-testid="excluir-acordo-bloqueio">{pv.bloqueio}</div>}
            <div className="rounded-lg border p-3 space-y-1" data-testid="excluir-acordo-resumo">
              <div className="flex justify-between gap-2"><span>Parcelas do acordo retiradas das listas ativas</span><b>{pv.parcelas_acordo}</b></div>
              <div className="flex justify-between gap-2"><span>Parcelas originais restauradas</span><b>{pv.parcelas_originais}</b></div>
              <div className="flex justify-between gap-2"><span>Pagamentos válidos no acordo (preservados)</span><b className="num-nowrap shrink-0">{pv.pagamentos_validos} · {brl(pv.total_recebido)}</b></div>
              {pv.pagamentos_validos > 0 && <>
                <div className="flex justify-between gap-2"><span>Abatido nas parcelas originais</span><b className="text-emerald-700">{brl(pv.abatimento_nas_originais)}</b></div>
                <div className="flex justify-between gap-2"><span>Saldo original resultante</span><b>{brl(pv.saldo_original_resultante)}</b></div>
                {pv.excedente > 0 && <div className="flex justify-between gap-2 text-amber-800"><span>Excedente recebido (acréscimo do acordo já pago)</span><b>{brl(pv.excedente)}</b></div>}
              </>}
              {pv.pagamentos_validos === 0 && <div className="flex justify-between gap-2"><span>Saldo original restaurado</span><b>{brl(pv.saldo_original_resultante)}</b></div>}
            </div>
            <p className="text-xs text-slate-500">Nenhum pagamento será apagado, estornado ou transferido. Os recebimentos continuam nos relatórios.</p>
          </div>
        )}
        <Textarea value={motivo} onChange={(e) => setMotivo(e.target.value)} rows={3} placeholder="Motivo da exclusão (obrigatório)" data-testid="excluir-acordo-motivo" />
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} data-testid="excluir-acordo-voltar">Voltar</Button>
          <Button onClick={confirmar} disabled={busy || !pv || !!pv.bloqueio || motivo.trim().length < 3} className="bg-red-600 hover:bg-red-700 text-white" data-testid="excluir-acordo-confirmar">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirmar exclusão"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const HistoricoAcordoDialog = ({ acordo, onClose }) => {
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/acordos/${acordo.id}/historico`).then((r) => setD(r.data)).catch(() => setD({ historico: [], auditoria: [] })); }, [acordo.id]);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="historico-acordo-dialog">
        <DialogHeader>
          <DialogTitle>Histórico do acordo — {acordo.cliente_nome}</DialogTitle>
          <DialogDescription>Criado em {fmtDate(acordo.created_at)} · situação atual: <b className="uppercase">{d?.situacao || acordo.situacao}</b></DialogDescription>
        </DialogHeader>
        {!d ? <div className="py-8 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div> : (
          <div className="space-y-2">
            {d.cancelamento && <div className="rounded border border-red-200 bg-red-50 p-2 text-xs text-red-800" data-testid="historico-acordo-cancelamento"><b>Cancelado</b> em {fdt(d.cancelamento.data)} por {d.cancelamento.por} — {d.cancelamento.motivo}{d.cancelamento.acerto_manual_necessario && " · acerto manual necessário"}</div>}
            {(d.historico || []).length === 0 && <p className="text-sm text-slate-400">Sem eventos registrados.</p>}
            {(d.historico || []).slice().reverse().map((h) => (
              <div key={h.id} className="rounded border p-2 text-xs" data-testid={`historico-acordo-evento-${h.evento}`}>
                <div className="flex justify-between gap-2"><b>{EVENTOS[h.evento] || h.evento}</b><span className="text-slate-500">{fdt(h.data)} · {h.usuario}</span></div>
                {h.motivo && <div className="text-slate-600">Motivo: {h.motivo}</div>}
                {h.antes && h.depois && <div className="text-slate-600 mono">saldo {brl(h.antes.saldo_aberto)} → {brl(h.depois.saldo_aberto)} · {h.antes.situacao} → {h.depois.situacao}</div>}
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
